import config from "environment";
import { postRequest, getRequest, patchRequest, deleteRequest, uploadClient, postCheckRequest, getCheckRequest, patchCheckRequest, deleteCheckRequest, apiClient } from "./client";
import {
  CheckCompany,
  CheckComponentRequest,
  CheckComponentResponse,
  CheckSignatory,
  CheckWorkplace,
  CheckPaySchedule,
  CheckFederalEIN,
  CheckBankAccount,
  CheckCompanyProvidedDocument,
  CheckForm,
  CheckFormStatus,
  ApiResponse,
  CheckEmployee,
  CheckContractor,
  CreateCheckBankAccountPayload,
  UpdateCheckBankAccountPayload,
  CheckEnrollmentProfile,
  CheckTaxParameter,
} from "winslow-core";

const { baseApi } = config;

export async function apiCheckGetCompany(
  checkCompanyId: string
): Promise<ApiResponse<CheckCompany> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}`;
  return await getCheckRequest<CheckCompany>(url, "API/apiCheckGetCompany");
}



export async function apiCheckGetCompanyFederalEINs(
  checkCompanyId: string
): Promise<ApiResponse<CheckFederalEIN[]> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/federal-eins`;
  return await getCheckRequest<CheckFederalEIN[]>(url, "API/apiCheckGetCompanyFederalEINs");
}

export async function apiCheckGetCompanyFederalEIN(
  checkCompanyId: string
): Promise<ApiResponse<string> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/federal-ein`;
  return await getCheckRequest<string>(url, "API/apiCheckGetCompanyFederalEIN");
}


export async function apiCheckGetEnrollmentProfile(
  checkCompanyId: string
): Promise<ApiResponse<CheckEnrollmentProfile> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/enrollment_profile`;
  return await getCheckRequest<CheckEnrollmentProfile>(url, "API/apiCheckGetEnrollmentProfile");
}

export async function apiCheckGetTaxParameters(
  checkCompanyId: string
): Promise<ApiResponse<CheckTaxParameter[]> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/company_tax_params`;
  return await getCheckRequest<CheckTaxParameter[]>(url, "API/apiCheckGetTaxParameters");
}

export async function apiCheckUpdateTaxParameter(
  checkCompanyId: string,
  taxParameterId: string,
  value: string
): Promise<ApiResponse<boolean> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/company_tax_params`;
  const payload = { id: taxParameterId, value };
  return await patchCheckRequest<{ id: string; value: string }, boolean>(url, payload, "API/apiCheckUpdateTaxParameter");
}

export async function apiCheckGetCompanyForm(
  checkCompanyId: string,
  checkFormId: string
): Promise<ApiResponse<CheckForm> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/forms/${checkFormId}`;
  return await getCheckRequest<CheckForm>(url, "API/apiCheckGetCompanyForm");
}

export async function apiCheckSubmitCompanyForm(
  checkCompanyId: string,
  checkFormId: string,
  parameters: Array<{ name: string; value: string }>
): Promise<ApiResponse<CheckFormStatus> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/forms/${checkFormId}/submit`;
  const payload = { parameters };
  return await postCheckRequest<{ parameters: Array<{ name: string; value: string }> }, CheckFormStatus>(
    url,
    payload,
    "API/apiCheckSubmitCompanyForm"
  );
}

export async function apiCheckRenderForm(
  checkFormId: string,
  parameters: Array<{ name: string; value: string }> = []
): Promise<Blob | null> {
  const url = `${baseApi}/payroll-check/forms/${checkFormId}/render`;
  
  try {
    const response = await apiClient.post(url, { parameters }, {
      headers: {
        Accept: "application/pdf",
      },
      responseType: "blob",
    });
    
    if (response.status === 200 && response.data) {
      return new Blob([response.data], { type: "application/pdf" });
    }
    
    return null;
  } catch (err) {
    console.log(`[API/apiCheckRenderForm] API threw an exception: ` + err);
    return null;
  }
}

export async function apiCheckCreateSignatory(
  checkCompanyId: string,
  signatoryData: Omit<CheckSignatory, "id">
): Promise<ApiResponse<CheckSignatory> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/create-signatory`;
  return await postCheckRequest(url, signatoryData, "API/apiCheckCreateSignatory");
}

export async function apiCheckGetSignatories(
  checkCompanyId: string
): Promise<ApiResponse<CheckSignatory[]> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/signatories`;
  return await getCheckRequest<CheckSignatory[]>(url, "API/apiCheckGetSignatories");
}

export async function apiCheckGetSignatory(
  checkSignatoryId: string
): Promise<ApiResponse<CheckSignatory> | null> {
  const url = `${baseApi}/payroll-check/signatories/${checkSignatoryId}`;
  return await getCheckRequest<CheckSignatory>(url, "API/apiCheckGetSignatory");
}

// NR not supported by check
// export async function apiCheckDeleteSignatory(
//   checkSignatoryId: string
// ): Promise<ApiResponse<boolean> | null> {
//   const url = `${baseApi}/payroll-check/signatories/${checkSignatoryId}`;
//   return await deleteCheckRequest<boolean>(url, "API/apiCheckDeleteSignatory");
// }

// NR not converted over yet as we are deprecating this
export async function apiCheckGetComponent(
  request: CheckComponentRequest
): Promise<CheckComponentResponse | null> {
  const url = `${baseApi}/payroll-check/component`;

  return await postRequest<CheckComponentRequest, CheckComponentResponse>(
    url,
    request,
    "API/apiCheckGetComponent"
  );
}   

export async function apiCheckGetCompanyOnboardLink(
  checkCompanyId: string,
  checkSignatoryId: string
): Promise<ApiResponse<string> | null> {

  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/${checkSignatoryId}/onboard-link`;
  return await getCheckRequest<string>(url, "API/apiCheckGetCompanyOnboardLink");
}

export async function apiCheckGetCompanyFilingComponent(
  checkCompanyId: string,
  checkSignatoryId: string
): Promise<ApiResponse<string> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/components/filing_authorization`;
  const payload = { signatory: checkSignatoryId };
  return await postCheckRequest<{ signatory: string }, string>(url, payload, "API/apiCheckGetCompanyFilingComponent");
}

export async function apiCheckGetCompanySignatoryAgreementsComponent(
  checkCompanyId: string,
  checkSignatoryId: string
): Promise<ApiResponse<string> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/components/signatory_agreements`;
  const payload = { signatory: checkSignatoryId };
  return await postCheckRequest<{ signatory: string }, string>(url, payload, "API/apiCheckGetCompanySignatoryAgreementsComponent");
}

export async function apiCheckGetActiveEmployeesAndContractorsCount(
  workspaceId: string
): Promise<ApiResponse<number> | null> {
  const url = `${baseApi}/payroll-check/workspace/${workspaceId}/active-check-employees-contractors-count`;
  return await getCheckRequest<number>(url, "API/apiCheckGetActiveEmployeesAndContractorsCount");
}


export async function apiCheckUpdateCompany(
  checkCompanyId: string,
  payload: Partial<CheckCompany>
): Promise<ApiResponse<CheckCompany> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}`;

  return await patchCheckRequest<Partial<CheckCompany>, CheckCompany>(
    url,
    payload,
    "API/apiCheckUpdateCompany"
  );
}


export async function apiCheckCreateWorkplace(
  checkCompanyId: string,
  payload: Omit<CheckWorkplace, "id">
): Promise<ApiResponse<CheckWorkplace> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/create-workplace`;
  return await postCheckRequest<Omit<CheckWorkplace, "id">, CheckWorkplace>(
    url,
    payload,
    "API/apiCheckCreateWorkplace"
  );
}

export async function apiCheckGetWorkplace(
  checkWorkplaceId: string
): Promise<ApiResponse<CheckWorkplace> | null> {
  const url = `${baseApi}/payroll-check/workplaces/${checkWorkplaceId}`;
  return await getCheckRequest<CheckWorkplace>(url, "API/apiCheckGetWorkplace");
}

export async function apiCheckUpdateWorkplace(
  checkWorkplaceId: string,
  payload: CheckWorkplace
): Promise<ApiResponse<CheckWorkplace> | null> {
  const url = `${baseApi}/payroll-check/workplaces/${checkWorkplaceId}`;
  return await patchCheckRequest<CheckWorkplace, CheckWorkplace>(
    url,
    payload,
    "API/apiCheckUpdateWorkplace"
  );
}

export async function apiCheckListCompanyWorkplaces(
  checkCompanyId: string
): Promise<ApiResponse<CheckWorkplace[]> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/workplaces`;
  return await getCheckRequest<CheckWorkplace[]>(url, "API/apiCheckListCompanyWorkplaces");
}

export async function apiCheckListCompanyPaySchedules(
  checkCompanyId: string
): Promise<ApiResponse<CheckPaySchedule[]> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/pay-schedules`;
  return await getCheckRequest<CheckPaySchedule[]>(url, "API/apiCheckListCompanyPaySchedules");
}

export async function apiCheckCreatePaySchedule(
  checkCompanyId: string,
  payload: Omit<CheckPaySchedule, "id">
): Promise<ApiResponse<CheckPaySchedule> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/create-pay-schedule`;
  return await postCheckRequest<Omit<CheckPaySchedule, "id">, CheckPaySchedule>(
    url,
    payload,
    "API/apiCheckCreatePaySchedule"
  );
}

export async function apiCheckUpdatePaySchedule(
  checkPayScheduleId: string,
  payload: CheckPaySchedule
): Promise<ApiResponse<CheckPaySchedule> | null> {
  const url = `${baseApi}/payroll-check/pay-schedules/${checkPayScheduleId}`;
  return await patchCheckRequest<CheckPaySchedule, CheckPaySchedule>(
    url,
    payload,
    "API/apiCheckUpdatePaySchedule"
  );
}


export async function apiCheckDeletePaySchedule(
  checkPayScheduleId: string
): Promise<ApiResponse<boolean> | null> {
  const url = `${baseApi}/payroll-check/pay-schedules/${checkPayScheduleId}`;
  return await deleteCheckRequest<boolean>(url, "API/apiCheckDeletePaySchedule");
}



export async function apiCheckUpdateFederalEIN(
  checkCompanyId: string,
  federalEIN: string
): Promise<ApiResponse<boolean> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/update-federal-ein`;
  return await patchCheckRequest<{ federal_ein: string }, boolean>(
    url, 
    { federal_ein: federalEIN }, 
    "API/apiCheckUpdateFederalEIN"
  );
}

export async function apiCheckListCompanyBankAccounts(
  checkCompanyId: string
): Promise<ApiResponse<CheckBankAccount[]> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/bank-accounts`;
  return await getCheckRequest<CheckBankAccount[]>(url, "API/apiCheckListCompanyBankAccounts");
}

export async function apiCheckListEmployeeBankAccounts(
  checkEmployeeId: string
): Promise<ApiResponse<CheckBankAccount[]> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}/bank-accounts`;
  return await getCheckRequest<CheckBankAccount[]>(url, "API/apiCheckListEmployeeBankAccounts");
}

export async function apiCheckListCompanyProvidedDocuments(
  checkCompanyId: string
): Promise<ApiResponse<CheckCompanyProvidedDocument[]> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/provided-documents`;
  return await getCheckRequest<CheckCompanyProvidedDocument[]>(url, "API/apiCheckListCompanyProvidedDocuments");
}

export async function apiCheckUploadCompanyProvidedDocument(
  checkCompanyProvidedDocumentId: string,
  file: File
): Promise<ApiResponse<CheckCompanyProvidedDocument> | null> {
  const url = `${baseApi}/payroll-check/company-provided-documents/${checkCompanyProvidedDocumentId}/upload`;
  
  // Validate file type
  const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
  }

  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await uploadClient.put(url, formData);
    const result = response.data;

    if (response.status === 500 || !result.data) {
      console.log(`[API/apiCheckUploadCompanyProvidedDocument] API call returned error: ${result.error}`);
      return null;
    }

    return {
      data: result.data as CheckCompanyProvidedDocument
    } as ApiResponse<CheckCompanyProvidedDocument>;
  } catch (err) {
    console.log(`[API/apiCheckUploadCompanyProvidedDocument] API threw an exception: ${err}`);
    return null;
  }
}

export async function apiCheckCreateBankAccount(
  checkCompanyId: string,
  payload: CreateCheckBankAccountPayload
): Promise<ApiResponse<CheckBankAccount> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/create-bank-account`;
  return await postCheckRequest<CreateCheckBankAccountPayload, CheckBankAccount>(
    url,
    payload,
    "API/apiCheckCreateBankAccount"
  );
}

export async function apiCheckUpdateBankAccount(
  checkBankAccountId: string,
  payload: UpdateCheckBankAccountPayload
): Promise<ApiResponse<CheckBankAccount> | null> {
  const url = `${baseApi}/payroll-check/bank-accounts/${checkBankAccountId}`;
  return await patchCheckRequest<UpdateCheckBankAccountPayload, CheckBankAccount>(
    url,
    payload,
    "API/apiCheckUpdateBankAccount"
  );
}

export async function apiCheckDeleteBankAccount(
  checkBankAccountId: string
): Promise<ApiResponse<boolean> | null> {
  const url = `${baseApi}/payroll-check/bank-accounts/${checkBankAccountId}`;
  return await deleteCheckRequest<boolean>(url, "API/apiCheckDeleteBankAccount");
}

export async function apiCheckGetBankAccountNumber(
  checkBankAccountId: string
): Promise<ApiResponse<string> | null> {
  // TODO: Implement API endpoint for fetching full bank account number
  const url = `${baseApi}/payroll-check/bank-accounts/${checkBankAccountId}/reveal-account-number`;
  return await getCheckRequest<string>(url, "API/apiCheckGetBankAccountNumber");
}

export async function apiCheckStartImplementation(
  checkCompanyId: string
): Promise<ApiResponse<boolean> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/start-implementation`;
  return await postCheckRequest<void, boolean>(
    url,
    undefined,
    "API/apiCheckStartImplementation"
  );
}

export async function apiCheckCreateEmployee(
  checkCompanyId: string,
  payload: Partial<CheckEmployee>
): Promise<ApiResponse<CheckEmployee> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/create-employee`;
  return await postCheckRequest<Partial<CheckEmployee>, CheckEmployee>(
    url,
    payload,
    "API/apiCheckCreateEmployee"
  );
}

export async function apiCheckGetEmployee(
  checkEmployeeId: string
): Promise<ApiResponse<CheckEmployee> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}`;
  return await getCheckRequest<CheckEmployee>(url, "API/apiCheckGetEmployee");
}

export async function apiCheckUpdateEmployee(
  checkEmployeeId: string,
  payload: CheckEmployee
): Promise<ApiResponse<CheckEmployee> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}`;
  return await patchCheckRequest<CheckEmployee, CheckEmployee>(
    url,
    payload,
    "API/apiCheckUpdateEmployee"
  );
}

export async function apiCheckUpdateEmployeeSSN(
  checkEmployeeId: string,
  ssn: string
): Promise<ApiResponse<CheckEmployee> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}/ssn`;
  return await patchCheckRequest<{ ssn: string }, CheckEmployee>(
    url,
    { ssn },
    "API/apiCheckUpdateEmployeeSSN"
  );
}

export async function apiCheckDeleteEmployee(
  checkEmployeeId: string
): Promise<ApiResponse<boolean> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}`;
  return await deleteCheckRequest<boolean>(url, "API/apiCheckDeleteEmployee");
}

export async function apiCheckListEmployeeForms(
  checkEmployeeId: string
): Promise<ApiResponse<CheckFormStatus[]> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}/forms`;
  return await getCheckRequest<CheckFormStatus[]>(url, "API/apiCheckListEmployeeForms");
}

export async function apiCheckGetForm(
  checkEmployeeId: string,
  checkFormId: string
): Promise<ApiResponse<CheckForm> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}/forms/${checkFormId}`;
  return await getCheckRequest<CheckForm>(url, "API/apiCheckGetForm");
}

export async function apiCheckSubmitEmployeeForm(
  checkEmployeeId: string,
  checkFormId: string,
  parameters: Array<{ name: string; value: string }>
): Promise<ApiResponse<CheckFormStatus> | null> {
  const url = `${baseApi}/payroll-check/employees/${checkEmployeeId}/forms/${checkFormId}/submit`;
  const payload = { parameters };
  return await postCheckRequest<{ parameters: Array<{ name: string; value: string }> }, CheckFormStatus>(
    url,
    payload,
    "API/apiCheckSubmitEmployeeForm"
  );
}

// Contractor API functions
export async function apiCheckGetContractor(
  checkContractorId: string
): Promise<ApiResponse<CheckContractor> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}`;
  return await getCheckRequest<CheckContractor>(url, "API/apiCheckGetContractor");
}

export async function apiCheckCreateContractor(
  checkCompanyId: string,
  payload: Partial<CheckContractor> & { ssn?: string; ein?: string | null }
): Promise<ApiResponse<CheckContractor> | null> {
  const url = `${baseApi}/payroll-check/company/${checkCompanyId}/create-contractor`;
  return await postCheckRequest<Partial<CheckContractor> & { ssn?: string; ein?: string | null }, CheckContractor>(
    url,
    payload,
    "API/apiCheckCreateContractor"
  );
}

export async function apiCheckUpdateContractor(
  checkContractorId: string,
  payload: CheckContractor
): Promise<ApiResponse<CheckContractor> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}`;
  return await patchCheckRequest<CheckContractor, CheckContractor>(
    url,
    payload,
    "API/apiCheckUpdateContractor"
  );
}

export async function apiCheckUpdateContractorSSN(
  checkContractorId: string,
  ssn: string
): Promise<ApiResponse<CheckContractor> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}/ssn`;
  return await patchCheckRequest<{ ssn: string }, CheckContractor>(
    url,
    { ssn },
    "API/apiCheckUpdateContractorSSN"
  );
}

export async function apiCheckUpdateContractorEIN(
  checkContractorId: string,
  ein: string
): Promise<ApiResponse<CheckContractor> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}/ein`;
  return await patchCheckRequest<{ ein: string }, CheckContractor>(
    url,
    { ein },
    "API/apiCheckUpdateContractorEIN"
  );
}

export async function apiCheckDeleteContractor(
  checkContractorId: string
): Promise<ApiResponse<boolean> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}`;
  return await deleteCheckRequest<boolean>(url, "API/apiCheckDeleteContractor");
}

export async function apiCheckListContractorBankAccounts(
  checkContractorId: string
): Promise<ApiResponse<CheckBankAccount[]> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}/bank-accounts`;
  return await getCheckRequest<CheckBankAccount[]>(url, "API/apiCheckListContractorBankAccounts");
}

export async function apiCheckListContractorForms(
  checkContractorId: string
): Promise<ApiResponse<CheckFormStatus[]> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}/forms`;
  return await getCheckRequest<CheckFormStatus[]>(url, "API/apiCheckListContractorForms");
}

export async function apiCheckGetContractorForm(
  checkContractorId: string,
  checkFormId: string
): Promise<ApiResponse<CheckForm> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}/forms/${checkFormId}`;
  return await getCheckRequest<CheckForm>(url, "API/apiCheckGetContractorForm");
}

export async function apiCheckSubmitContractorForm(
  checkContractorId: string,
  checkFormId: string,
  parameters: Array<{ name: string; value: string }>
): Promise<ApiResponse<CheckFormStatus> | null> {
  const url = `${baseApi}/payroll-check/contractors/${checkContractorId}/forms/${checkFormId}/submit`;
  const payload = { parameters };
  return await postCheckRequest<{ parameters: Array<{ name: string; value: string }> }, CheckFormStatus>(
    url,
    payload,
    "API/apiCheckSubmitContractorForm"
  );
}